import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, CssBaseline, AppBar, Toolbar, Typography,
  Backdrop, CircularProgress, Button
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TextField } from "@mui/material";
import SideButton from './SideButton';
import Header from './Header';
 

const drawerWidth = 270;
const sliderWidth = 42.1;
const batchSize = 100;
const delayBetweenBatches = 5000;

const ROUTE_CONFIG = {
  searcholl: { displayName: 'Searcholl', endpoint: 'searcholl' },
  yrr_mynewtab: { displayName: 'YRR MyNewTab', endpoint: 'yrrMynewtab' },
  yrr_wundrfly: { displayName: 'YRR Wundrfly', endpoint: 'yrrWundrfly' },
  mynewtab: { displayName: 'MyNewTab', endpoint: 'mynewtab' },
  wundrfly: { displayName: 'Wundrfly', endpoint: 'wundrfly' },
  ollreview: { displayName: 'Oll Review', endpoint: 'ollreview' },
  vayunewtab: { displayName: 'Vayu NewTab', endpoint: 'vayunewtab' },
  searchhello: { displayName: 'Searchhello', endpoint: 'searchhello' },
  safesearchengine: { displayName: 'Safe Search Engine', endpoint: 'sse' },
  safesearchengine_net: { displayName: 'SafeSearchEngine.net', endpoint: 'ssenet' },
  safesearchengine_org: { displayName: 'SafeSearchEngine.org', endpoint: 'sseorg' },
  taskticks: { displayName: 'Task Ticks', endpoint: 'taskticks' },
  lukall: { displayName: 'Lukall', endpoint: 'lukall' },
  vayutab: { displayName: 'VayuTab', endpoint: 'vayutab' },
  speedytest: { displayName: 'SpeedyTest', endpoint: 'stg' },
  amazeai: { displayName: 'AmazeAI', endpoint: 'amazeai' },
  lukkol: { displayName: 'Lukkol', endpoint: 'lukkol' }
};

const columns = [
  { field: 'terms', headerName: 'Search Terms', width: 500 },
  { field: 'Date', headerName: 'Date & Time', width: 400 }
];

const Main = () => {
  const location = useLocation();
  const [currentRoute, setCurrentRoute] = useState('');
  const [searchTerms, setSearchTerms] = useState([]);
  const [filterTerms, setFilterTerms] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [open, setOpen] = useState(false);

  const oneWeekAgo = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 4);
    return date;
  }, []);

  useEffect(() => {
    const route = location.pathname.split('/admin/')[1]?.toLowerCase() || '';
    if (!route || !ROUTE_CONFIG[route]) return;
    setCurrentRoute(route);
    fetchAndProcessData(route);
  }, [location.pathname]);

  const fetchAndProcessData = async (route) => {
    setOpen(true);
    try {
      const { endpoint } = ROUTE_CONFIG[route];
      const response = await axios.get(`https://server.taskticks.com/api/items/${endpoint}`);
      const data = response.data || [];

      const formattedData = data.map(item => {
        const dateObj = new Date(item.Date);
        return {
          id: item._id,
          terms: item.terms,
          Date: dateObj.toLocaleString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit'
          }),
          rawDate: dateObj
        };
      });

      const idsToDelete = formattedData.filter(item => item.rawDate < oneWeekAgo).map(item => item.id);
      setOpen(false);
      if (idsToDelete.length > 0) {
        deleteItemsInBatches(idsToDelete, endpoint);
      }
      
      setSearchTerms(formattedData);
      setFilterTerms(formattedData);
    } catch (err) {
      console.error('Fetch error:', err);
      toast.error(`Failed to fetch ${ROUTE_CONFIG[route]?.displayName} data`);
    } finally {
      setOpen(false);
    }
  };

  const deleteItemsInBatches = async (ids, endpoint) => {
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      try {
        await axios.delete(`https://server.taskticks.com/api/items/${endpoint}`, {
          headers: { 'Content-Type': 'application/json' },
          data: { ids: batch }
        });
        await new Promise(res => setTimeout(res, delayBetweenBatches));

        
      } catch (err) {
        console.error('Batch deletion failed:', err);
      }
    }
  };

  const handleSearch = useCallback((e) => {
    const searchTerm = e.target.value.trim().toLowerCase().replace(/\s/g, '');
    if (!searchTerm) return setFilterTerms(searchTerms);
    const results = searchTerms.filter(item =>
      item.terms?.trim().toLowerCase().replace(/\s/g, '').includes(searchTerm)
    );
    setFilterTerms(results);
  }, [searchTerms]);

useEffect(() => {
  if (!startDate && !endDate) {
    setFilterTerms(searchTerms);
    return;
  }

  const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
  const end = endDate ? new Date(`${endDate}T23:59:59`) : null;

  const filtered = searchTerms.filter(item => {
    const itemDate = new Date(item.rawDate);
    return (!start || itemDate >= start) && (!end || itemDate <= end);
  });

  setFilterTerms(filtered);

  if (startDate && endDate && filtered.length === 0) {
    toast.info("No search queries found for the selected dates");
  }
}, [startDate, endDate, searchTerms]);


  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    toast.success('Date filters cleared');
  };

  if (!ROUTE_CONFIG[currentRoute]) return <div>Loading...</div>;

  return (
    <>
      <ToastContainer />
      {open && (
        <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={open}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Box sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: { xs: `calc(100% - ${sliderWidth}px)`, md: `calc(100% - ${drawerWidth}px)` },
            ml: { xs: `${sliderWidth}px`, md: `${drawerWidth}px` }
          }}
        >
          <Toolbar sx={{ background: '#fff' }}>
            <Typography variant="h6" sx={{ color: '#333', fontWeight: 'bold', flexGrow: 1 }}>
              Analytics Tool
            </Typography>
            <Header />
          </Toolbar>
        </AppBar>

        <SideButton />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 5,
            px: { xs: 5, md: 5 },
            zIndex: 1,
            overflowX: 'hidden'
          }}
        >
          <Toolbar />
          <Box className="container pt-1">
            <Typography variant="h6">
              <b>{ROUTE_CONFIG[currentRoute]?.displayName} Search Terms</b>
              <span style={{ float: 'right' }}>
                <b>Total: {searchTerms.length}</b>
              </span>
            </Typography>

            <Box className="filterSection" sx={{ my: 2, display: 'flex', justifyContent: 'space-between' }}>
              <input
                type="search"
                placeholder="Search"
                onChange={handleSearch}
                style={{ padding: '6px 12px', width: '300px' }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                
                <Box display="flex" gap={1} alignItems="center">
  <TextField
    label="From"
    type="date"
    value={startDate}
    InputLabelProps={{ shrink: true }}
    onChange={(e) => setStartDate(e.target.value)}
    inputProps={{ max: endDate || undefined }}
  />

  <TextField
    label="To"
    type="date"
    value={endDate}
    InputLabelProps={{ shrink: true }}
    onChange={(e) => setEndDate(e.target.value)}
    inputProps={{ min: startDate || undefined }}
  />
</Box>
<Button variant="contained" color="error" onClick={clearDateFilter}>
                  Clear
                </Button>
              </Box>
            </Box>
      {filterTerms.length > 0 ?  <DataGrid
              rows={filterTerms}
              columns={columns}
              checkboxSelection
              sx={{ minHeight: '400px' }}
              initialState={{
                pagination: { paginationModel: { pageSize: 10, page: 0 } }
              }}
              pageSizeOptions={[5, 10, 20]}
            /> : <div className='text-center' style={{height: '200px', display: 'flex', justifyContent: 'center', alignItems: 'middle'}}>
                  <p style={{margin: 'auto'}}>No data found</p>
                 </div>}
           
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Main;
